import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Source, Layer, Popup, useMap } from 'react-map-gl/maplibre';

import { useIsAdmin } from '../auth/useIsAdmin';
import { useTopLayers } from '../map-gl/useTopLayers';
import {
  ASSAY_RAMP, CATEGORIES, DRILL_ICON_EMPTY, INTERACTIVE, ORDER, PROSPECT_WHITE, SRC,
  REACH_M, TICK_M, activeKinds, drillIcon, drillPpbOf, prospectLayers,
} from './prospectStyle';
import { fetchAll, boundsToCql, EMPTY } from './prospectWfs';
import './ProspectOverlay.css';

/**
 * Prospect reports as an overlay on the main map — admin only.
 *
 * Served from GeoServer WFS (`ne:wa_prospect_*`, six views over
 * au_pathfinder.prospect_features), per viewport. This replaces a static bundle
 * of GeoJSON files in the front-end bucket, which had to be re-published by hand
 * for every new report and had drifted 12 reports behind what the pipeline had
 * actually produced. One source of truth now: what is in the table is what is on
 * the map, the moment the pipeline writes it.
 *
 * WHAT THE POPUP CAN AND CANNOT SHOW. The published views do not carry the
 * report's prose — no target name, setting, chips or citations; `refs` is a
 * text[] that GeoServer will not serve at all.
 *
 * `ne:wa_prospect_darkdata` is deliberately NOT among the categories drawn. Those
 * 51 polygons are report footprints whose sampling never reached the assay
 * tables — a backlog of work to recover, not data — and at 51 overlapping
 * footprints they hazed the goldfields without telling a prospector anything he
 * could walk to. The table, the GeoServer layer and prospect.py's `darkdata`
 * command are all untouched; re-add it here as its own sidebar toggle if it is
 * wanted back, rather than folding it into this one checkbox. So the popup shows what the views
 * DO carry (the site code, the grade, the class, the quoted line, the body note)
 * and links out to the full HTML report for the rest. That link is the only
 * remaining dependency on the bucket, and it is a document, not data.
 */
const REPORT_BASE = (process.env.REACT_APP_PROSPECT_BASE || '/admin/prospect').replace(/\/$/, '');
// The same report page the soil, drill-hole and reporter popups link to. The
// GeoView host this used to point at does not resolve at all, so every citation
// was a dead link. A-numbers go on the query bare: the app stores them as
// integers and that is what ReportDetails expects.
const WAMEX = 'https://wamex.dmp.wa.gov.au/Wamex/Search/ReportDetails?ANumber=';

/**
 * Below this the viewport is most of the goldfields and the fetch is neither
 * useful nor cheap — 475 features statewide today, but this grows with every
 * report and there is nothing legible about the whole state at z6 anyway.
 */
const MIN_ZOOM = 7;
/** Pans arrive in bursts; one fetch per settled viewport is enough. */
const DEBOUNCE_MS = 400;
/** Click tolerance, in screen pixels either side of the cursor. */
const HIT_SLOP_PX = 5;

/**
 * What makes two hits the same MARK.
 *
 * queryRenderedFeatures returns a feature once per layer it is drawn in, and a
 * drill hole's rows all sit on one point, so the raw hits over-count badly. The
 * keys are the same identities the rest of this file works in: a hole is its
 * number within its report, a finding is its number within its report, and a
 * soil sample — which carries no id of its own — is its printed grid tie.
 */
const markKey = (p = {}) => {
  if (p.kind === 'drill_hole') return `drill/${p.anumber}/${p.hole}`;
  if (p.kind === 'finding') return `finding/${p.anumber}/${p.n}`;
  if (p.kind === 'soil_sample') return `soil/${p.anumber}/${p.local_n}/${p.local_e}/${p.au_ppb}`;
  // Nothing recognisable: treat every hit as its own mark rather than silently
  // collapsing two things a reader can see are different.
  return `other/${p.kind}/${JSON.stringify(p)}`;
};

/**
 * One line per candidate in the picker.
 *
 * The kind alone does not say which one you want — three drill holes can be
 * under one cursor — so each row carries the number a reader would recognise
 * and the value they are choosing between.
 */
const markLabel = (p = {}) => {
  if (p.kind === 'drill_hole') {
    const reach = Number(p.reach_ppb);
    return {
      title: p.hole ? `Drill hole ${p.hole}` : 'Drill hole',
      detail: reach >= 0
        ? `${(reach / 1000).toFixed(2)} g/t in the first ${REACH_M} m`
        : `nothing in the first ${REACH_M} m`,
    };
  }
  if (p.kind === 'soil_sample') {
    const ppb = p.au_ppb;
    return {
      title: 'Soil sample',
      detail: ppb === null || ppb === undefined || ppb === '' ? 'value unread' : `${ppb} ppb`,
    };
  }
  if (p.kind === 'finding') {
    return {
      title: p.n ? `Finding ${p.n}` : 'Finding',
      detail: CLASS_LABEL[p.class] || p.class || '',
    };
  }
  return { title: 'Mark', detail: '' };
};

export function ProspectOverlay({ overlayVisibility, prospectFilters }) {
  const isAdmin = useIsAdmin();
  const visible = !!overlayVisibility?.prospect && isAdmin;

  // Which of soils / drill holes / findings the sidebar has selected.
  //
  // Reduced to a string BEFORE anything memoises on it. The sidebar hands down
  // a fresh filters object on every one of its renders, and `cats` feeds `load`,
  // which the fetch effect depends on — so memoising on the object itself would
  // refetch the whole viewport whenever anything else in the sidebar moved.
  const kindsKey = activeKinds(prospectFilters).join(',');
  const cats = useMemo(
    () => CATEGORIES.filter((c) => kindsKey.split(',').includes(c.key)),
    [kindsKey],
  );

  const [data, setData] = useState({});
  const [failed, setFailed] = useState([]);
  const [tooFarOut, setTooFarOut] = useState(false);
  const [picked, setPicked] = useState(null);
  const { current: map } = useMap();

  // The in-flight request, so a pan that lands while one is running cancels it
  // rather than racing it to setState.
  const inflight = useRef(null);

  const load = useCallback(async () => {
    if (!map) return;
    if (map.getZoom() < MIN_ZOOM) { setTooFarOut(true); return; }
    setTooFarOut(false);

    if (inflight.current) inflight.current.abort();
    const ctrl = new AbortController();
    inflight.current = ctrl;
    try {
      const { data: next, failed: bad } = await fetchAll(boundsToCql(map.getBounds()), ctrl.signal, cats);
      if (ctrl.signal.aborted) return;
      setData(next);
      setFailed(bad);
    } catch (err) {
      if (ctrl.signal.aborted) return;
      // Every category threw, or something outside them did. Say so rather than
      // leaving the last viewport's marks on screen as if they were current.
      setData({});
      setFailed(cats.map((c) => c.key));
    } finally {
      if (inflight.current === ctrl) inflight.current = null;
    }
  }, [map, cats]);

  // Fetch on enable and on every settled viewport, never on mount: a user who
  // does not turn this on never pays for it.
  useEffect(() => {
    if (!map || !visible) return undefined;
    let timer = 0;
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(load, DEBOUNCE_MS);
    };
    load();
    map.on('moveend', schedule);
    return () => {
      clearTimeout(timer);
      map.off('moveend', schedule);
      if (inflight.current) { inflight.current.abort(); inflight.current = null; }
    };
  }, [map, visible, load]);

  // The downhole triangles, one per colour band.
  //
  // Same shape and size as the drill-hole overlay's marker so a recovered hole
  // reads as the same kind of thing, but bordered BLACK where that one is
  // white. That inversion is the only thing distinguishing a value somebody
  // read off a scanned plate — good to tens of metres — from a surveyed one,
  // so it has to survive any later tidying of this drawing code.
  useEffect(() => {
    if (!map || !visible) return undefined;
    // WHITE is the no-value one: a hole carrying nothing to colour, which the
    // drill layer asks for by id instead of a colour band. White rather than
    // an outline because a punched-out triangle reads as a rendering fault at
    // small sizes, while a solid white one reads as a hole that was drilled
    // and printed no number - which is what it is.
    const bands = [...ASSAY_RAMP.map((colour, i) => [drillIcon(i), colour]), [DRILL_ICON_EMPTY, PROSPECT_WHITE]];
    const ids = bands.map(([id]) => id);
    bands.forEach(([id, colour]) => {
      const canvas = document.createElement('canvas');
      canvas.width = 24;
      canvas.height = 24;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, 24, 24);
      // The casing, drawn first and one pixel proud on every side.
      ctx.beginPath();
      ctx.moveTo(12, 20);
      ctx.lineTo(4, 4);
      ctx.lineTo(20, 4);
      ctx.closePath();
      ctx.fillStyle = '#000000';
      ctx.fill();
      // The band colour on top, white for the no-value one. Every triangle is
      // filled, so they all carry the same weight on the map and only the
      // fill differs.
      ctx.beginPath();
      ctx.moveTo(12, 19);
      ctx.lineTo(5, 5);
      ctx.lineTo(19, 5);
      ctx.closePath();
      ctx.fillStyle = colour;
      ctx.fill();

      const img = new Image();
      img.onload = () => {
        // The map can be torn down between this effect and the decode.
        try {
          if (!map.getStyle?.()) return;
          if (map.hasImage(id)) map.removeImage(id);
          map.addImage(id, img);
        } catch {
          // style gone; nothing to register against
        }
      };
      img.src = canvas.toDataURL('image/png');
    });

    return () => {
      // hasImage reaches through the style to the image manager, so it THROWS
      // rather than returning false once the map has been torn down — and this
      // cleanup runs on exactly that path.
      try {
        if (!map.getStyle?.()) return;
      } catch {
        return;
      }
      ids.forEach((id) => {
        try { if (map.hasImage(id)) map.removeImage(id); } catch { /* already gone */ }
      });
    };
  }, [map, visible]);

  // Click handling is registered here rather than on the parent Map so that
  // turning the overlay off takes its handler with it.
  useEffect(() => {
    if (!map || !visible) return undefined;
    const onClick = (e) => {
      // queryRenderedFeatures throws on a layer that is not on the map, and
      // these come and go with the category data.
      const present = INTERACTIVE.filter((id) => map.getLayer(id));
      if (!present.length) return;
      // THE EXACT POINT IS THE PRIMARY TEST, and deliberately so. Querying a
      // box instead of a point was a regression: it is a different code path
      // in MapLibre (a polygon intersection against each symbol's collision
      // box rather than a point test) and it stopped returning the drill
      // triangles altogether. The slop is now only a SECOND chance, tried when
      // the exact point hits nothing, so a dead-centre click can never resolve
      // to anything different from what it did before the slop existed.
      let hits = map.queryRenderedFeatures(e.point, { layers: present });
      if (!hits.length) {
        hits = map.queryRenderedFeatures([
          [e.point.x - HIT_SLOP_PX, e.point.y - HIT_SLOP_PX],
          [e.point.x + HIT_SLOP_PX, e.point.y + HIT_SLOP_PX],
        ], { layers: present });
      }
      if (!hits.length) return;
      // EVERY CANDIDATE UNDER THE CURSOR, not just the topmost.
      //
      // Marks overlap constantly here: a recovered soil traverse runs straight
      // through a 20 m drill grid with findings numbered over both, and at z14
      // three of them can sit inside the same few pixels. Taking the top layer
      // made everything beneath it unreachable — no click could ever select it —
      // which is the same fault the tenement overlays had before they learned to
      // ask. So when more than one distinct mark is under the cursor, the reader
      // chooses; when only one is, nothing changes and it opens directly.
      const seen = new Map();
      hits.forEach((h) => {
        const props = h.properties || {};
        const key = markKey(props);
        // One entry per MARK. The same feature comes back once per layer it is
        // drawn in, and a drill hole's rows all share its point.
        if (!seen.has(key)) seen.set(key, { key, props, layer: h.layer?.id });
      });

      // INTERACTIVE is topmost-first, so the list reads in the order the marks
      // are stacked on screen: what is on top is what the reader was aiming at.
      const order = (c) => {
        const i = present.indexOf(c.layer);
        return i === -1 ? present.length : i;
      };
      const candidates = [...seen.values()]
        .sort((a, b) => order(a) - order(b))
        .map((c) => ({
          ...c,
          // A hole's intervals are sibling rows in the same collection, carrying
          // the same `hole` and the same position, so the downhole table is
          // built from the viewport data already in hand — no second request,
          // and nothing to fetch when the popup opens.
          siblings: c.props.kind === 'drill_hole' && c.props.hole
            ? ((data.drillholes || EMPTY).features || [])
              .map((f) => f.properties || {})
              .filter((q) => q.hole === c.props.hole && q.anumber === c.props.anumber)
            : [],
        }));
      if (!candidates.length) return;
      // One mark: straight to its detail, exactly as before.
      setPicked({ lngLat: e.lngLat, candidates, chosen: candidates.length === 1 ? 0 : null });
    };
    map.on('click', onClick);
    return () => { map.off('click', onClick); };
    // `data` is read inside the handler to gather a hole's sibling intervals,
    // so the handler must be re-registered when it changes — otherwise the
    // closure keeps the empty first-render value and every table comes up bare.
  }, [map, visible, data]);

  useEffect(() => { if (!visible) { setPicked(null); setData({}); } }, [visible]);

  // Switching a category off takes its marks out of the popup with them. `kind`
  // is what the popup switches on, so that is what is checked — and leaving them
  // would leave a panel describing marks no longer anywhere on the map.
  useEffect(() => {
    if (!picked) return;
    const kinds = new Set(cats.map((c) => c.kind));
    const left = picked.candidates.filter((c) => kinds.has(c.props?.kind));
    if (left.length === picked.candidates.length) return;
    if (!left.length) { setPicked(null); return; }
    // Keep the reader on the mark they chose if it survived the change.
    const chosenKey = picked.chosen === null ? null : picked.candidates[picked.chosen]?.key;
    const idx = chosenKey ? left.findIndex((c) => c.key === chosenKey) : -1;
    setPicked({
      ...picked,
      candidates: left,
      chosen: idx >= 0 ? idx : (left.length === 1 ? 0 : null),
    });
  }, [cats, picked]);

  // Above every other overlay, and above the block grid. A target is the
  // conclusion drawn over the evidence — buried under a tenement fill or a
  // soil tile it cannot be read or clicked.
  useTopLayers('prospect', 20, ORDER, map, visible);

  // `hideEmpty` reaches the layer builders, which turn it into a MapLibre filter
  // on the soil and drill layers. It is a view over data already in hand — no
  // request depends on it — so unlike the category selection it costs nothing to
  // flip back and forth.
  const hideEmpty = !!prospectFilters?.hideEmpty;
  // Whether the popup lists intervals below the reach cut-off. A view over rows
  // already fetched, like hideEmpty — the deep rows are in the source either
  // way, because the table needs to be able to count what it is not showing.
  const showDeep = !!prospectFilters?.showDeep;
  const sources = useMemo(() => cats.map((cat) => ({
    cat,
    layers: prospectLayers[cat.key]({ hideEmpty }),
  })), [cats, hideEmpty]);

  if (!visible) return null;

  return (
    <>
      {sources.map(({ cat, layers }) => (
        <Source key={cat.key} id={SRC(cat.key)} type="geojson" data={data[cat.key] || EMPTY}>
          {layers.map((l) => <Layer key={l.id} {...l} />)}
        </Source>
      ))}
      {tooFarOut && (
        <div className="prospect-warn prospect-info">Zoom in to load prospect reports.</div>
      )}
      {!tooFarOut && failed.length > 0 && (
        <div className="prospect-warn">
          {failed.length === cats.length
            ? 'Prospect layers unavailable.'
            : `Some prospect layers unavailable (${failed.join(', ')}).`}
        </div>
      )}
      {picked && (
        <Popup longitude={picked.lngLat.lng} latitude={picked.lngLat.lat}
               anchor="bottom" maxWidth="380px" closeOnClick={false}
               onClose={() => setPicked(null)} className="prospect-popup">
          {picked.chosen === null ? (
            <ProspectPicker
              candidates={picked.candidates}
              onChoose={(i) => setPicked((prev) => ({ ...prev, chosen: i }))}
            />
          ) : (
            <ProspectPopup
              p={picked.candidates[picked.chosen].props}
              siblings={picked.candidates[picked.chosen].siblings}
              showDeep={showDeep}
              // Only offered when there was a choice to begin with, so a single
              // mark does not grow a back link to nowhere.
              onBack={picked.candidates.length > 1
                ? () => setPicked((prev) => ({ ...prev, chosen: null }))
                : null}
            />
          )}
        </Popup>
      )}
    </>
  );
}

/** What each collar flag means, in the reader's terms. */
const FLAG_LABEL = {
  not_drawn_on_plate: 'Position unverified — no plate draws this hole',
  conflict_unresolved: 'Sources disagree on this collar — unresolved',
  label_provisional: 'Hole number provisional; the collar itself is clean',
  outside_tenement: 'Falls just outside E 77/374, within positional error',
};

/** Human labels for the finding classes the views serve. */
const CLASS_LABEL = {
  find: 'Recorded find',
  working: 'Old working',
  intercept: 'Drill intercept',
  anomaly: 'Anomaly',
  geology: 'Geology',
  access: 'Access',
};

/**
 * Which of the marks under the cursor did you mean.
 *
 * Shown only when a click resolves to more than one. Choosing replaces this with
 * that mark's detail, and the detail offers a way back — the same shape the
 * tenement overlays use, so overlapping things behave the same way everywhere.
 */
function ProspectPicker({ candidates, onChoose }) {
  return (
    <div className="pp-picker">
      <h3>{candidates.length} marks here</h3>
      <ul>
        {candidates.map((c, i) => {
          const { title, detail } = markLabel(c.props);
          return (
            <li key={c.key}>
              <button type="button" onClick={() => onChoose(i)}>
                <span className="pp-pick-title">{title}</span>
                {detail ? <span className="pp-dim"> · {detail}</span> : null}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ProspectPopup({ p, siblings = [], showDeep = false, onBack = null }) {
  const report = p.report_slug ? `${REPORT_BASE}/${p.report_slug}.html` : null;

  const body = () => {
    if (p.kind === 'target') {
      return (
        <>
          {/* The view serves no name, so the site code is the heading. It is
              what the report prints beside the same circle, which is what makes
              the map and the report pair up. */}
          <h3>Target {p.site_id || ''}</h3>
          <p className="pp-grade">{p.grade}{p.origin ? ` · ${p.origin}` : ''}</p>
          <Text text={p.body} />
        </>
      );
    }
    if (p.kind === 'finding') {
      return (
        <>
          <h3>
            Finding {p.n}
            {p.class ? <span className="pp-class">{CLASS_LABEL[p.class] || p.class}</span> : null}
          </h3>
          {/* Orthogonal to class, and the single most important thing on the
              mark: this evidence is in a scanned report and in no assay table. */}
          {p.undigitised === true || p.undigitised === 'true' ? (
            <p className="pp-warn">Not in any assay table — read from the report.</p>
          ) : null}
          {/* A finding's class says which medium it is: an `intercept` came out
              of a hole and is quoted in g/t, anything else is surface and is
              quoted in ppb. */}
          <Grade ppb={p.au_ppb} gt={p.au_gt} downhole={p.class === 'intercept'} />
          <Text text={p.body} />
          {p.quote ? <blockquote>{p.quote}</blockquote> : null}
          <Anumber a={p.anumber} />
        </>
      );
    }
        if (p.kind === 'drill_hole') {
      const flags = (p.flags || '').split(',').filter(Boolean);
      return (
        <>
          <h3>{p.hole}</h3>
          {/* THE HEADLINE IS THE MARK'S OWN NUMBER. `reach_ppb` is what the
              fetch computed and what the icon is coloured by, so the popup
              cannot disagree with the triangle the reader just clicked. It is
              the best value in the first REACH_M metres — not the whole-hole
              maximum, which for WAB204 sits at ~48-52 m and would send somebody
              to ground they cannot work. */}
          {Number(p.reach_ppb) >= 0 ? (
            <p className="pp-grade">
              {(Number(p.reach_ppb) / 1000).toFixed(2)} g/t Au
              <Depth from={p.reach_from_m} to={p.reach_to_m} inferred={p.reach_inferred} />
            </p>
          ) : p.all_below_detection ? (
            <p className="pp-dim">Every interval printed below detection.</p>
          ) : (
            /* Not barren — nothing WORKABLE. The distinction is the whole point
               of the depth rule, and a reader who takes this for "no gold here"
               has been misled by the very thing meant to protect them. */
            <p className="pp-dim">Nothing printed in the first {REACH_M} m.</p>
          )}
          {/* Every assayed interval, shallowest first — the headline is one
              number, this is where the gold sits. Below REACH_M is hidden unless
              the sidebar asks for it, and the count is always stated so a hole
              cut off at 30 m on screen never reads as a hole that stopped at
              30 m in the ground. */}
          <DownholeTable rows={siblings} showDeep={showDeep} deepRows={p.deep_rows} />
          {showDeep && p.au_deep_ppb != null && Number(p.au_deep_ppb) > Number(p.reach_ppb || 0) ? (
            <p className="pp-warn">
              Best anywhere down the hole is {(Number(p.au_deep_ppb) / 1000).toFixed(2)} g/t
              at about {Math.round((p.deep_from_m - 1) * TICK_M)}–{Math.round(p.deep_from_m * TICK_M)} m
              — below working depth.
            </p>
          ) : null}
          <p className="pp-grade">
            {p.total_depth_m ? `${p.total_depth_m} m` : 'depth not on sheet'}
            {p.dec_deg ? ` · dip ${p.dec_deg}°` : ''}
            {/* The writer put "Grid West" across the degrees field, so this is
                text and is not a bearing. Do not render it as one. */}
            {p.azimuth ? ` · ${p.azimuth}` : ''}
          </p>
          {/* The number, not the essay. The paragraph that used to follow it —
              grid tie, drill block, inferred depths — said the same three things
              on every hole, so it read as boilerplate and stopped being read at
              all. The ± IS the warning. Where a depth is inferred rather than
              printed, the depth itself says so, next to the value it qualifies. */}
          <p className="pp-warn">Position ±{p.accuracy_m} m</p>
          <dl>
            <dt>Local grid</dt><dd>{p.local_n} N, {p.local_e} E</dd>
            <dt>AMG51</dt><dd>{p.amg51_e}, {p.amg51_n}</dd>
            {p.cu_ppm != null ? (
              <><dt>Cu / Fe / Cr</dt><dd>{p.cu_ppm} / {p.fe_pct} / {p.cr_ppm}<span className="pp-dim"> (units not stated on sheet)</span></dd></>
            ) : null}
            {p.logged_date ? <><dt>Logged</dt><dd>{p.logged_date}</dd></> : null}
          </dl>
          {flags.length ? (
            <p className="pp-warn">{flags.map((f) => FLAG_LABEL[f] || f).join(' · ')}</p>
          ) : null}
          <Anumber a={p.anumber} />
        </>
      );
    }
    if (p.kind === 'soil_sample') {
      return (
        <>
          <h3>Soil sample</h3>
          {p.au_ppb != null ? (
            <p className="pp-grade">
              {p.au_ppb} ppb Au
              {p.au_repeat_ppb != null ? ` · repeat ${p.au_repeat_ppb} ppb` : ''}
            </p>
          ) : (
            <p className="pp-dim">Below detection — a measurement, not a gap.</p>
          )}
          {/* BCL/BLEG is a bulk-leach method over a different sample mass from a
              fire assay. Naming it stops anyone pooling these with the soil
              overlay's digitised ppb. */}
          <p className="pp-dim">{p.method} (bulk leach) — not directly comparable with fire-assay ppb.</p>
          <dl>
            <dt>Local grid</dt><dd>{p.local_n} N, {p.local_e} E</dd>
          </dl>
          <p className="pp-warn">Position ±{p.accuracy_m} m</p>
          <p className="pp-recovery">Not in any assay table — read from the report.</p>
          <Anumber a={p.anumber} />
        </>
      );
    }
        if (p.kind === 'detector_line' || p.kind === 'corridor') {
      // prospect_lines is the one view that serves `kind`, because it merges the
      // two — so here it is the data talking, not the stamp.
      return (
        <>
          <h3>{p.name || (p.kind === 'corridor' ? 'Corridor' : 'Detector line')}</h3>
          {p.site_id ? <p className="pp-grade">{p.site_id}</p> : null}
          <p className="pp-dim">
            {p.kind === 'corridor'
              ? 'Argued across reports, not mapped.'
              : 'Where to walk — not a finding.'}
          </p>
        </>
      );
    }
    if (p.kind === 'aoi') {
      return (
        <>
          <h3>{p.report_target || 'Report area'}</h3>
          <p className="pp-dim">The area this report was built for.</p>
        </>
      );
    }
    return <h3>{p.report_target || p.kind}</h3>;
  };

  return (
    <div className="pp">
      {onBack ? (
        <button type="button" className="pp-back" onClick={onBack}>
          ← the other marks here
        </button>
      ) : null}
      {body()}
      <p className="pp-src">
        <span>{p.report_target || p.report_slug}</span>
        {report ? <a href={report} target="_blank" rel="noreferrer">open the full report ↗</a> : null}
      </p>
    </div>
  );
}

/**
 * A soil ppb and a downhole g/t are different quantities and the table keeps
 * them in different columns for exactly that reason. Never add them — a client
 * that conflates them is a silent 1000x.
 *
 * WHICH UNIT IS SHOWN IS A PROPERTY OF THE MEDIUM, NOT OF THE SOURCE.
 * Everywhere in this product a hole is quoted in g/t (the drill-hole overlay
 * renders au_gpt as g/t) and a surface sample in ppb (the soil overlay renders
 * au_ppb as ppb). A prospector reads those two scales for two different things.
 * So `downhole` picks the unit here, converting where the source printed the
 * other one, rather than echoing whatever the scanned table happened to use.
 */
const Grade = ({ ppb, gt, downhole }) => {
  const p = ppb == null || ppb === '' ? null : Number(ppb);
  const g = gt == null || gt === '' ? null : Number(gt);
  if (downhole) {
    const v = g != null ? g : (p != null ? p / 1000 : null);
    return v == null ? null : <p className="pp-grade">{v.toFixed(2)} g/t Au</p>;
  }
  const v = p != null ? p : (g != null ? g * 1000 : null);
  return v == null ? null : <p className="pp-grade">{Math.round(v)} ppb Au</p>;
};

/**
 * Every assayed interval of one hole, shallowest first, quoted in g/t.
 *
 * WHY THIS IS NOT A DEPTH TABLE. Only A45655 prints from_m/to_m (148 of 437
 * intervals). A42187's 289 intervals carry `ordinal` alone — position in the
 * hole — because their depths were read off tick spacing on a scanned section
 * and none is printed. So the depth column shows a real interval where one
 * exists and the ordinal where one does not, and says which it is. Rendering
 * an inferred position as "0–4 m" would invent a measurement.
 *
 * Unit follows the house rule (see DRILL_PPB in prospectStyle.js): prefer
 * au_ppb, fall back to au_gt, then convert ONCE for display. au_ppb of 0 is a
 * real reading, so the emptiness test is on the string form.
 */
const num = (v) => (v == null || v === '' ? null : Number(v));

/**
 * A depth, said as precisely as the source allows.
 *
 * The A45655 tables print from/to, so those are exact. The A42187 sections print
 * none, so the depth is inferred from tick spacing and is hedged with "~" — a
 * reader deciding where to dig is entitled to know which of the two they have.
 */
const Depth = ({ from, to, inferred }) => {
  if (from === null || from === undefined) {
    return <span className="pp-dim"> · depth not printed for this interval</span>;
  }
  const round = (v) => (inferred ? Math.round(v) : v);
  const span = to === null || to === undefined ? `${round(from)} m` : `${round(from)}–${round(to)} m`;
  return <span className="pp-dim"> · {inferred ? '~' : ''}{span}</span>;
};

const DownholeTable = ({ rows, showDeep, deepRows = 0 }) => {
  const intervals = rows
    .filter((r) => num(r.ordinal) != null || num(r.from_m) != null)
    // Below the reach cut-off is hidden unless asked for. The count still gets
    // stated underneath, because a table that silently stops at 30 m reads as a
    // hole that stopped at 30 m.
    .filter((r) => showDeep || !r.row_deep)
    .map((r) => {
      const ppb = num(r.au_ppb);
      const gt = ppb != null ? ppb / 1000 : num(r.au_gt);
      return { ...r, gt, depthKnown: num(r.from_m) != null };
    })
    .sort((a, b) => {
      const ad = num(a.from_m) ?? num(a.ordinal) ?? 0;
      const bd = num(b.from_m) ?? num(b.ordinal) ?? 0;
      return ad - bd;
    });

  const hidden = showDeep ? 0 : deepRows;
  if (!intervals.length) {
    return hidden
      ? <p className="pp-dim">{hidden} assayed {hidden === 1 ? 'interval' : 'intervals'} below {REACH_M} m, hidden.</p>
      : null;
  }

  const anyInferred = intervals.some((r) => !r.depthKnown);

  return (
    <>
      <table className="pp-downhole">
        <thead>
          <tr><th>{anyInferred ? 'Interval' : 'Depth'}</th><th>Au g/t</th></tr>
        </thead>
        <tbody>
          {intervals.map((r, i) => (
            <tr key={`${r.hole}-${r.ordinal ?? r.from_m ?? i}`}>
              <td>
                {r.depthKnown
                  ? `${r.from_m}–${r.to_m} m`
                  : <span className="pp-dim">#{r.ordinal}</span>}
              </td>
              <td>
                {r.gt != null
                  ? r.gt.toFixed(3)
                  : <span className="pp-dim">{r.below_detection ? 'bd' : '—'}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {anyInferred ? (
        <p className="pp-dim">
          Interval number, not depth — none is printed on the section.
        </p>
      ) : null}
      {hidden ? (
        <p className="pp-dim">
          {hidden} assayed {hidden === 1 ? 'interval' : 'intervals'} below {REACH_M} m, hidden.
        </p>
      ) : null}
    </>
  );
};

const Text = ({ text }) => (text ? <p>{text}</p> : null);

const Anumber = ({ a }) => (a
  ? (
    <p className="pp-cites">
      <a href={`${WAMEX}${a}`} target="_blank" rel="noreferrer">A{a}</a>
    </p>
  )
  : null);

export default ProspectOverlay;
